import { Button, Modal, message } from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";

import styles from "@/components/common/OTPModal/OtpModal.module.css";
import { OTP_LENGTH, OTP_RESEND_COOLDOWN, Regex } from "@/constant";
import useCooldown from "@/hooks/useCooldown";

interface OtpModalProps {
  open: boolean;
  onSubmit?: (otp: string) => Promise<void>;
  onSubmitResend?: () => Promise<void>;
  onClose?: () => void;
}

const OtpModal: React.FC<OtpModalProps> = ({ open, onSubmit, onSubmitResend, onClose }) => {
  const { cooldown, isCooldownActive, startCooldown, executeWithCooldown } =
    useCooldown(OTP_RESEND_COOLDOWN);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const [otp, setOtp] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      startCooldown();
      setOtp("");
    }
  }, [open, startCooldown]);

  const handleChange = (value: string, index: number): void => {
    if (!Regex.OTP_REGEX.test(value)) return;

    const newOtp = otp.split("");
    newOtp[index] = value;
    const joined = newOtp.join("");
    setOtp(joined);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number): void => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = otp.split("");
        newOtp[index] = "";
        setOtp(newOtp.join(""));
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const newOtp = otp.split("");
        newOtp[index - 1] = "";
        setOtp(newOtp.join(""));
      }
    }
  };

  const handleSubmitOTP = async (): Promise<void | boolean> => {
    if (otp.length !== OTP_LENGTH) return message.error("Invalid OTP");
    try {
      setLoading(true);
      await onSubmit?.(otp);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async (): Promise<void> => {
    try {
      await executeWithCooldown(async () => {
        await onSubmitResend?.();
        message.success("OTP resent successfully");
      });
    } catch (error: unknown) {
      console.error(error);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      className={styles.otpModal}
      width={600}
    >
      <div className={styles.modalContainer}>
        <span className={styles.header}>Verify OTP</span>
        <div className={styles.infoContainer}>
          <span>An OTP has been sent to your registered email and mobile number.</span>
          <span>Please enter the OTP proceed.</span>
        </div>
        <div>
          <span className={styles.otpNote}>This OTP is valid for the next 2 hours</span>
        </div>
      </div>
      <div className={styles.otpWrapper}>
        {Array.from({ length: OTP_LENGTH }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputsRef.current[index] = el)}
            type="text"
            maxLength={1}
            value={otp[index] || ""}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={styles.otpInput}
          />
        ))}
      </div>
      <Button
        className={styles.applyButton}
        onClick={() => void handleSubmitOTP()}
        loading={loading}
        block
      >
        Submit
      </Button>
      <Button
        type="link"
        className={`${styles.resendButton} ${isCooldownActive ? styles.disabledResend : ""}`}
        onClick={() => void handleResendOTP()}
        disabled={isCooldownActive}
      >
        {isCooldownActive ? `Resend OTP (${cooldown}s)` : "Resend OTP"}
      </Button>
    </Modal>
  );
};

export default OtpModal;
